import IMG_BG_DESKTOP from '~/assets/images/kv/ovenbreak/bg_desktop.jpg';
import IMG_BG_MOBILE from '~/assets/images/kv/ovenbreak/bg_mobile.jpg';
import VIDEO_KV_PC_H264 from '~/assets/images/kv/ovenbreak/kv_pc_h264.mp4';
import VIDEO_KV_PC_HEVC from '~/assets/images/kv/ovenbreak/kv_pc_hevc.mp4';
import IMG_LOGO_EN from '~/assets/images/kv/ovenbreak/logo_en.png';
import IMG_LOGO_JA from '~/assets/images/kv/ovenbreak/logo_ja.png';
import IMG_LOGO_KO from '~/assets/images/kv/ovenbreak/logo_ko.png';
import IMG_LOGO_TH from '~/assets/images/kv/ovenbreak/logo_th.png';
import IMG_LOGO_ZH_HANT from '~/assets/images/kv/ovenbreak/logo_zh-Hant.png';

import styled from '@emotion/styled';
import { rem } from 'polished';
import React from 'react';
import { useLocale } from '~/shared/libs/gatsby-plugin-i18n';

import StoreButtons from '~/components/StoreButtons';
import { css, scaledCss } from '~/css';
import { useFrontmatter } from '~/shared/contexts/frontmatter';
import { mq } from '~/shared/utils/mq';

const PlaceholderBackgroundPC = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `url(${IMG_BG_DESKTOP})`,
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  zIndex: -2,
});

const PlaceholderBackgroundMobile = styled.div({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundImage: `url(${IMG_BG_MOBILE})`,
  backgroundPosition: 'center center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  zIndex: -2,
});

const PcVideoBackground = styled.div({
  position: 'relative',
  overflow: 'hidden',
  display: 'none',
  '@media (min-width: 640px)': {
    display: 'block',
  },
  minHeight: '700px',
  height: '40.2083vw',
  maxHeight: '2000px',
});

const MobileVideoBackground = styled.div({
  position: 'relative',
  overflow: 'hidden',
  display: 'block',
  '@media (min-width: 640px)': {
    display: 'none',
  },
  minHeight: '500px',
  height: '150vw',
});

const VideoElement = styled.video({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '100%',
  minHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'cover',
  zIndex: -1,
});

const ContentLayout = styled.div(
  mq({
    position: 'absolute',
    top: ['50%', 'unset'],
    bottom: ['unset', 0],

    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: rem(8),

    paddingBottom: rem(42),
    width: '100%',
  }),
);

const LogoImage = styled.img({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
});

const getLogoByLocale = (locale: string) => {
  switch (locale.toLowerCase()) {
    case 'ko':
      return IMG_LOGO_KO;
    case 'ja':
      return IMG_LOGO_JA;
    case 'th':
      return IMG_LOGO_TH;
    case 'zh-hant':
      return IMG_LOGO_ZH_HANT;
    case 'zh-hans':
      return IMG_LOGO_ZH_HANT;
    case 'en':
    default:
      return IMG_LOGO_EN;
  }
};

const OvenbreakKeyVisual: React.FC = () => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const frontmatter = useFrontmatter();
  const locale = useLocale();

  const { logo } = frontmatter.top;
  const logoSrc = getLogoByLocale(locale);

  React.useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div css={css({ position: 'relative' })}>
      <PcVideoBackground>
        <PlaceholderBackgroundPC />
        <ContentLayout>
          <div
            css={css({
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: [logo.pc.h * 0.8, logo.pc.h * 0.8, logo.pc.h * 0.8, logo.pc.h],
              ...(logo.pc.margin && { marginBottom: logo.pc.margin }),
            })}
          >
            <LogoImage src={logoSrc} alt='logo' loading='eager' />
          </div>
          <StoreButtons />
        </ContentLayout>
      </PcVideoBackground>
      <MobileVideoBackground>
        <PlaceholderBackgroundMobile />

        <ContentLayout>
          <div
            css={scaledCss({
              marginBottom: -48,
              width: logo.mobile.w,
              height: logo.mobile.h,
              ...(logo.mobile.margin && { marginBottom: logo.mobile.margin }),
            })}
          >
            <LogoImage src={logoSrc} alt='logo' loading='eager' />
          </div>
          <StoreButtons />
        </ContentLayout>
      </MobileVideoBackground>
      {isLoaded && (
        <VideoElement autoPlay loop muted playsInline preload='auto'>
          <source src={VIDEO_KV_PC_HEVC} type={'video/mp4; codec=hev1.1.6.H123.B0'} />
          <source src={VIDEO_KV_PC_H264} type={'video/mp4; codecs=avc1.42E01E'} />
        </VideoElement>
      )}
    </div>
  );
};

export default OvenbreakKeyVisual;
